import { types, getParent } from 'mobx-state-tree';
import { flow } from 'mobx';
import { user, game, utils } from "@myplay/all";
import { toast } from "@myplay/ui";

import I18N from '../../I18N';
import { toTitleCase, capitalize } from '../../utils/stringManipulations';
import{ VIDEOS_BULK, AWAY, GCS_UPLOADED, CHECKSUM } from "../../utils/constants";

const videosModule = game.videos;
const tagTasksModule = game.tagTasks;
const clubsModule = user.clubs;

const VideosStore = types
  .model("Videos", {
    videos: types.maybeNull(types.array(types.frozen({}))), 
    hasMoreVideosToLoad: true, 
    uploadPercent: types.maybeNull(types.number), 
    ongoingUploadTeamName: types.maybeNull(types.string), 
    ongoingUploadDate: types.maybeNull(types.frozen({}))
  })
  .actions(self => ({
    getClubVideos: flow(function* getClubVideos(clubId, skip = 0, filters = {}){
      try {
        const { ClubsStore } = getParent(self);
        const currentClubId = clubId || ClubsStore.currentClub.club._id;
        if (skip === 0) {
          self.videos = [];
        };
        const videos = yield clubsModule.getClubVideos(currentClubId, VIDEOS_BULK, skip, filters);
        self.addVideos(videos); 
      } catch (error) {
        console.log(error);
      }
    }), 
    addVideos(videos) {
      const videosCopy = [...self.videos, ...videos];
      self.videos = [...videosCopy];

      if (self.videos.length === 0 ) {
        self.videos = null;
      }
    },
    deleteVideo: flow(function* deleteVideo(videoId) {
      try {
        yield videosModule.deleteVideoContainer(videoId);
        self.removeVideoFromStore(videoId);
      } catch (error) {
        console.log(error);
      }
    }), 
    removeVideoFromStore(videoId) {
      const { TeamsStore } = getParent(self);
      const indexOfVideo = self.videos.findIndex(video => video._id === videoId);
      const videosCopy = [...self.videos];
      TeamsStore.getTeamRecentVideos(videosCopy[indexOfVideo].team._id);
      videosCopy.splice(indexOfVideo, 1);
      self.videos = [ ...videosCopy ];
    },
    downloadVideo: flow(function* downloadVideo(videoId, videoContainerId) {
      try {
        const { UserStore, NotificationsStore } = getParent(self);
        yield videosModule.downloadVideo(
          videoContainerId,
          videoId,
          UserStore.data._id
        );
        NotificationsStore.getActiveTasks();
        toast.success(capitalize(I18N.t("DOWNLOAD_STARTED")));
      } catch (error) {
        toast.error(toTitleCase(I18N.t("SOMETHING_WENT_WRONG")));
        console.log(error);
      }
    }),
    changeVideoTitle: flow(function* changeVideoTitle(videoId, title){
      try {
        yield videosModule.updateVideoContainer(videoId, { title });
      } catch(error) {
        console.log(error);
      }
    }), 
    updateGameScore: flow(function* updateGameScore(videoId, score, team){
      try {
        if (team === AWAY) {
          yield videosModule.setAwayTeamScore(videoId, score);
          return ;
        };

        yield videosModule.setHomeTeamScore(videoId, score);

      } catch(error) {
        console.log(error);
      }
    }),
    shareVideoContainer: flow(function* shareVideoContainer(videoContainerId, teamToShareWith){
      try {
        const { _id, name } = teamToShareWith;
        const { sharedLinkId } = yield utils.setShareItem("video", videoContainerId);
        yield videosModule.insertVideoFromShare(sharedLinkId, _id);
        toast.success(capitalize(`${I18N.t("GAME_SHARED_SUCCESS")}${name}`)); 
      } catch (error) {
        toast.error(toTitleCase(I18N.t("SOMETHING_WENT_WRONG")));
        console.log(error);
      }
    }),
    assignAssistant: flow(function* assignAssistant(assistantId, videoContainerId){
      try {
        const { UserStore } = getParent(self);
        yield tagTasksModule.createGameTagTask(
          UserStore.data._id,
          assistantId,
          videoContainerId
        );
      } catch (error) {
        toast.error(toTitleCase(I18N.t("SOMETHING_WENT_WRONG")));
        console.log(error);
      }
    }), 
    uploadVideoFromComputer: flow(function* uploadVideoFromComputer(fileName, team) {
      try {
        const {UserStore: { data: { _id } } } = getParent(self);
        const res = yield videosModule.uploadDirectExternalVideo(
          team._id,
          _id,
          fileName,
          CHECKSUM
        );

        const { uploadUrl, requestId } = res;
        toast.success(capitalize(I18N.t("STARTED_UPLOAD")))
        return { uploadUrl, requestId };
      } catch (error) {
        console.log(error);
      }
    }), 
    updateUploadVideoRequest: flow(function* updateUploadVideoRequest(requestId){
      try {
        yield videosModule.updateUploadVideoRequest(requestId, GCS_UPLOADED);
      } catch (error) {
        toast.error(capitalize(I18N.t("SOMETHING_WENT_WRONG")));
        console.log(error);
      }
    }),
    validateYoutubeLink: flow(function* validateYoutubeLink(videoUrl){
      try {
        const videoInfo = yield videosModule.getVideoToUploadInfo(videoUrl);
        return videoInfo;
      } catch (error) {
        console.log(error);
      }
    }), 
    uploadVideoFromYoutube: flow(function* uploadVideoFromYoutube(videoUrl, team) {
      try {
        const { UserStore: { data: { _id } } } = getParent(self);
        yield videosModule.uploadExternalVideo(
          team._id,
          _id,
          videoUrl
        );
        toast.success(capitalize(I18N.t("STARTED_UPLOAD_YOUTUBE")));
      } catch(error) {
        toast.error(capitalize(I18N.t("SOMETHING_WENT_WRONG")));
        console.log(error);
      }
    }), 
    updateUploadPercent(percent, teamName) {
      const { TeamsStore } = getParent(self);
      if (percent) {
        self.uploadPercent = percent;
        self.ongoingUploadTeamName = teamName;
        self.ongoingUploadDate = new Date();
      }

      if (percent === null) {
        self.uploadPercent = 100;
        setTimeout(()=> {
          const id = TeamsStore.getTeamIdByName(self.ongoingUploadTeamName,4,0,true);
          TeamsStore.getTeamRecentVideos(id);
          self.getClubVideos();
          self.resetPercent();
        }, 5000);
      }
    }, 
    resetPercent() {
      self.uploadPercent = null;
      self.ongoingUploadTeamName = null;
      self.ongoingUploadDate = null;
    }
  }))

export default VideosStore;
